import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, CheckCircle2, ClipboardCheck, LogIn, ListChecks } from 'lucide-react';
import type { UserProfile } from '@/lib/api';
import { formatDate, formatDateTime, formatCertificateDate } from '@/lib/dates';
import { ROLE_LABELS } from '@/lib/labels';
import { Badge, Card, PageHeader } from '@/components/ui';

const ACTIVITY = {
  enrolled: { icon: BookOpen, verb: 'Started the course' },
  completed_course: { icon: CheckCircle2, verb: 'Completed the course' },
  quiz: { icon: ClipboardCheck, verb: 'Took' },
  certificate: { icon: Award, verb: 'Earned a certificate for' },
  joined: { icon: LogIn, verb: 'Joined' },
} as const;

/**
 * A person's profile for their admins: who they are, their organizations,
 * courses, quiz results, certificates and recent activity. Org admins' view
 * only includes what happened in their org tree (the API scopes it).
 */
export function UserProfileView({
  profile: p,
  back,
  scopeNote,
  actions,
}: {
  profile: UserProfile;
  back: { to: string; label: string };
  /** Explains what this view covers, e.g. "Activity in IIT Hyderabad and its colleges." */
  scopeNote: string;
  /** Admin controls shown above the numbers (an org's role and removal). */
  actions?: ReactNode;
}) {
  const s = p.summary;
  return (
    <div className="space-y-6">
      <PageHeader
        title={p.user.displayName ?? p.user.email}
        description={p.user.displayName ? p.user.email : undefined}
        back={back}
      />
      <div className="flex flex-wrap gap-2">
        {p.user.isPlatformAdmin && <Badge>Platform admin</Badge>}
        {p.user.suspendedAt && <Badge>Suspended</Badge>}
        <Badge>Joined Nirvik Ready {formatDate(p.user.createdAt)}</Badge>
        <Badge>
          {p.lastActiveAt
            ? `Last active ${formatDate(p.lastActiveAt)}`
            : 'No learning activity yet'}
        </Badge>
        {p.user.timeZone && <Badge>{p.user.timeZone}</Badge>}
      </div>
      <p className="text-sm text-muted-foreground">{scopeNote}</p>
      {actions}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          ['Courses started', s.coursesEnrolled],
          ['Courses completed', s.coursesCompleted],
          ['Quizzes taken', s.quizzesTaken],
          ['Certificates', s.certificates],
        ].map(([label, value]) => (
          <Card key={label}>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="mt-1 text-3xl font-semibold tabular-nums">{value}</p>
          </Card>
        ))}
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-6">
          <Card title="Courses">
            {p.courses.length === 0 ? (
              <Empty>No courses yet.</Empty>
            ) : (
              <ul className="divide-y divide-border">
                {p.courses.map((c) => (
                  <li key={c.courseId} className="py-3 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <Link to={`/c/${c.slug}`} className="font-medium hover:text-primary">
                        {c.title}
                      </Link>
                      <span className="text-muted-foreground">
                        {c.completedAt ? `Completed ${formatDate(c.completedAt)}` : `${c.percent}%`}
                      </span>
                    </div>
                    <progress
                      max={100}
                      value={c.percent}
                      aria-label={`${c.title} progress`}
                      className="mt-2 h-1.5 w-full appearance-none overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Started {formatDate(c.enrolledAt)} · last active {formatDate(c.lastActiveAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Quizzes and assessments">
            {p.quizzes.length === 0 ? (
              <Empty>No quiz results yet.</Empty>
            ) : (
              <ul className="divide-y divide-border">
                {p.quizzes.map((q) => (
                  <li
                    key={q.quizId}
                    className="flex flex-wrap items-baseline justify-between gap-2 py-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{q.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {q.courseAssessment ? 'Course final assessment · ' : ''}
                        {q.attempts} attempt{q.attempts === 1 ? '' : 's'} · last{' '}
                        {formatDate(q.lastAt)}
                      </span>
                    </span>
                    <span className="tabular-nums">
                      {q.bestPercent === null ? '—' : `Best ${q.bestPercent}%`}
                      {q.passed && <Badge>Passed</Badge>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Certificates">
            {p.certificates.length === 0 ? (
              <Empty>No certificates yet.</Empty>
            ) : (
              <ul className="divide-y divide-border">
                {p.certificates.map((c) => (
                  <li
                    key={c.code}
                    className="flex flex-wrap items-baseline justify-between gap-2 py-3 text-sm"
                  >
                    <span className="min-w-0">
                      {c.isPublic ? (
                        <Link
                          to={`/verify/${c.code}`}
                          className="block font-medium hover:text-primary"
                        >
                          {c.title}
                        </Link>
                      ) : (
                        <span className="block font-medium">{c.title}</span>
                      )}
                      <span className="block text-xs text-muted-foreground">
                        {c.kind === 'course' ? 'Course' : c.kind === 'event' ? 'Event' : 'Quiz'} ·{' '}
                        {c.issuerName} · {formatCertificateDate(c.issuedAt, c.timeZone)}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {!c.isPublic && <Badge>Private</Badge>}
                      <span className="text-muted-foreground">ID {c.code}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <aside className="min-w-0 space-y-6">
          <Card title="Organizations">
            {p.memberships.length === 0 ? (
              <Empty>Not in any organization.</Empty>
            ) : (
              <ul className="space-y-3 text-sm">
                {p.memberships.map((m) => (
                  <li key={`${m.orgId}-${m.joinedAt}`}>
                    <p className="font-medium">{m.orgName}</p>
                    <p className="text-xs text-muted-foreground">
                      {ROLE_LABELS[m.role]} · joined {formatDate(m.joinedAt)}
                      {m.leftAt ? ` · left ${formatDate(m.leftAt)}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Recent activity">
            {p.activity.length === 0 ? (
              <Empty>Nothing yet.</Empty>
            ) : (
              <ol className="space-y-3 text-sm">
                {p.activity.map((a, i) => {
                  const { icon: Icon, verb } = ACTIVITY[a.kind] ?? {
                    icon: ListChecks,
                    verb: '',
                  };
                  return (
                    <li key={i} className="flex gap-3">
                      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0">
                        <span className="block">
                          {verb} <span className="font-medium">{a.title}</span>
                          {a.detail && a.kind === 'quiz' ? `: ${a.detail}` : ''}
                          {a.detail && a.kind === 'joined'
                            ? ` as ${ROLE_LABELS[a.detail as keyof typeof ROLE_LABELS] ?? a.detail}`
                            : ''}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {formatDateTime(a.at)}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}
